// Carry Roll SessionList — saved sessions + trends (8.6).

import { X } from 'lucide-react';
import { useStore } from '../../store';
import { ClubCategory, SessionType } from '@carry-roll/shared-types';
import { mToYd } from '@carry-roll/shared-logic';
import { useAlert } from '@ascend/alert';

export function SessionList() {
  const sessions = useStore((s) => s.sessions);
  const activeSessionId = useStore((s) => s.activeSessionId);
  const createSession = useStore((s) => s.createSession);
  const setActiveSession = useStore((s) => s.setActiveSession);
  const deleteSession = useStore((s) => s.deleteSession);
  const distanceUnit = useStore((s) => s.distanceUnit);
  const { confirm, prompt } = useAlert();
  const conv = (m: number) => (distanceUnit === 'yd' ? Math.round(mToYd(m)) : Math.round(m));

  const handleNew = async () => {
    const name = await prompt({
      title: 'New session',
      label: 'Session name',
      defaultValue: 'New session',
      placeholder: 'e.g. Range — Driver',
      validate: (v) => (v.trim().length === 0 ? 'Name cannot be empty' : null),
    });
    if (name !== null) {
      createSession(name.trim(), SessionType.Outdoor, ClubCategory.Driver);
    }
  };

  const handleDelete = async (id: string, name: string, shotCount: number) => {
    const ok = await confirm({
      title: 'Delete session?',
      message: `"${name}" contains ${shotCount} shot${shotCount === 1 ? '' : 's'}. This cannot be undone.`,
      danger: true,
      confirmLabel: 'Delete',
    });
    if (ok) deleteSession(id);
  };

  if (sessions.length === 0) {
    return (
      <div className="panel" data-testid="session-list" style={{ color: 'var(--color-text-muted)' }}>
        No sessions yet. Save a shot from the Calculator or import a CSV.
        <div className="mt-2">
          <button type="button" className="btn btn-primary" onClick={handleNew}>
            + New session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel" data-testid="session-list">
      <h3 className="subsection-title">
        Sessions
      </h3>
      <ul className="text-sm">
        {sessions.map((s) => (
          <li
            key={s.id}
            className="flex justify-between items-center"
            style={{
              padding: 'var(--space-2)',
              borderBottom: '1px solid var(--color-border)',
              cursor: 'pointer',
              backgroundColor: s.id === activeSessionId ? 'var(--color-surface-2)' : 'transparent',
              minWidth: 0,
            }}
          >
            <button
              type="button"
              className="flex-1 text-left bg-transparent border-none"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
              onClick={() => setActiveSession(s.id)}
              aria-pressed={s.id === activeSessionId}
            >
              {s.name} <span className="badge">{s.type}</span>
            </button>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {s.shots.length} shots · avg {s.avgCarryM != null ? `${conv(s.avgCarryM)} ${distanceUnit}` : '—'}
            </span>
            <button
              type="button"
              className="text-xs ml-2 bg-transparent border-none"
              aria-label={`Delete ${s.name}`}
              onClick={() => handleDelete(s.id, s.name, s.shots.length)}
              style={{ color: 'var(--color-danger)' }}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-2">
        <button type="button" className="btn btn-primary" onClick={handleNew}>
          + New session
        </button>
      </div>
    </div>
  );
}
